import React, { FC, useCallback, useRef, useState } from 'react';

import {
	BUTTON_TYPES,
	EvButton,
	EvCaptionDefault,
	EvDivider,
	EvFlexColumn,
	EvFlexRow,
	EvSection,
	EvSvgIcon,
	FilePlus,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import { Logger } from 'src/common/services/Logger';

import { FileUploadProps } from './FileUpload.types';
import { isAcceptableUploadFile } from './FileUpload.utils';

export const FileUpload: FC<FileUploadProps> = ({
	labels,
	validation,
	onFileContent,
	onTouched,
	externalError,
	onClearExternalError,
	buttonDataAutomationId,
	inputDataAutomationId,
	errorDataAutomationId,
	showDivider = true
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [localUploadError, setLocalUploadError] = useState<string | undefined>();

	const handleUploadClick = useCallback(() => {
		onTouched?.();
		fileInputRef.current?.click();
	}, [onTouched]);

	const handleFileChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			event.target.value = '';
			if (!file) {
				return;
			}

			onTouched?.();
			setLocalUploadError(undefined);
			onClearExternalError?.();

			if (!isAcceptableUploadFile(file, validation)) {
				setLocalUploadError(labels.invalidFileType);
				return;
			}

			if (file.size > validation.maxFileSizeBytes) {
				setLocalUploadError(labels.fileTooLarge);
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				const text = typeof reader.result === 'string' ? reader.result : '';
				onFileContent(text);
			};
			reader.onerror = () => {
				Logger.error('FileUpload: failed to read file', { name: file.name });
				setLocalUploadError(labels.fileReadError);
			};
			reader.readAsText(file);
		},
		[validation, labels, onFileContent, onTouched, onClearExternalError]
	);

	const uploadError = localUploadError ?? externalError;

	return (
		<>
			<input
				ref={fileInputRef}
				className="sr-only"
				type="file"
				accept={validation.accept}
				tabIndex={-1}
				aria-label={labels.inputAria}
				data-automation-id={inputDataAutomationId}
				onChange={handleFileChange}
			/>
			{showDivider && (
				<EvFlexRow flex="1" padding={['NONE', 'MEDIUM']}>
					<EvDivider orientation="vertical" />
				</EvFlexRow>
			)}
			<EvFlexColumn gap="SMALL">
				<EvButton
					type={BUTTON_TYPES.SECONDARY}
					title={labels.button}
					onClick={handleUploadClick}
					dataAutomationId={buttonDataAutomationId}
					isFullWidth
				>
					<EvFlexRow wrap="NOWRAP" justify="START">
						<EvSvgIcon icon={FilePlus} isSmall />
						{labels.button}
					</EvFlexRow>
				</EvButton>
				{uploadError && (
					<EvSection ariaLive="assertive">
						<EvCaptionDefault color={TEXT_COLOR.RED} dataAutomationId={errorDataAutomationId}>
							{uploadError}
						</EvCaptionDefault>
					</EvSection>
				)}
			</EvFlexColumn>
		</>
	);
};
